import React, {useState} from 'react';
import {Linking, Pressable, StyleSheet, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';

import {useAuthStore} from '@/entities/session/auth-store';
import {messageOf} from '@/shared/lib/errors';
import type {AuthNav} from '@/app/routes';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {Icon} from '@/shared/ui/icons';
import {AppInput} from '@/shared/ui/input';
import {Logo} from '@/shared/ui/logo';
import {Screen} from '@/shared/ui/screen';
import {AppText, useLinkText} from '@/shared/ui/text';
import {controls, spacing, useTheme} from '@/shared/ui/theme';
import {PRIVACY_URL, TERMS_URL, TERMS_VERSION} from '@/shared/config/legal';

/**
 * Self-registration: имя, почта, пароль и согласие с пользовательским соглашением и
 * политикой конфиденциальности. Сервер без согласия ничего не заводит (422
 * `terms_required`), а с ним — отправляет код на почту (202, без токенов). Дальше
 * экран кода: сессию выдаёт только подтверждение.
 */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD = 8;

const styles = StyleSheet.create({
  body: {justifyContent: 'center', gap: spacing(5), paddingVertical: spacing(8)},
  hero: {alignItems: 'center', gap: spacing(3)},
  heroText: {alignItems: 'center', gap: spacing(1), paddingHorizontal: spacing(4)},
  centered: {textAlign: 'center'},
  form: {gap: spacing(4)},
  link: {minHeight: controls.lg, justifyContent: 'center', alignSelf: 'center'},
  consent: {flexDirection: 'row', alignItems: 'flex-start', gap: spacing(2)},
  box: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  consentText: {flex: 1},
});

export function RegisterScreen() {
  const {t} = useTranslation();
  const p = useTheme();
  const link = useLinkText();
  const nav = useNavigation<AuthNav>();
  const register = useAuthStore(s => s.register);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const name = fullName.trim();
    const mail = email.trim();

    if (!name) {
      setError(t('auth.errors.fullNameRequired'));
      return;
    }
    if (!mail) {
      setError(t('auth.errors.emailRequired'));
      return;
    }
    if (!EMAIL.test(mail)) {
      setError(t('auth.errors.emailInvalid'));
      return;
    }
    // Checked here as well as on the server: a round trip to be told "too short" is
    // a round trip we already knew the answer to.
    if (password.length < MIN_PASSWORD) {
      setError(t('auth.errors.passwordTooShort'));
      return;
    }
    if (!accepted) {
      setError(t('auth.errors.termsRequired'));
      return;
    }

    setError(null);
    setBusy(true);
    try {
      await register({
        email: mail,
        password,
        fullName: name,
        acceptTerms: true,
        termsVersion: TERMS_VERSION,
      });
      nav.navigate('VerifyEmail');
    } catch (e) {
      setError(messageOf(e, t('errors.network')));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen scroll width="narrow" style={styles.body} testID="register-screen">
      <View style={styles.hero}>
        <Logo width={180} />
        <View style={styles.heroText}>
          <AppText variant="display" style={styles.centered}>
            {t('auth.register.title')}
          </AppText>
          <AppText variant="body" color={p.mutedForeground} style={styles.centered}>
            {t('auth.register.subtitle')}
          </AppText>
        </View>
      </View>

      <Card padding={5} style={styles.form}>
        <AppInput
          testID="register-fullname"
          label={t('auth.register.fullName')}
          placeholder={t('auth.register.fullNamePlaceholder')}
          value={fullName}
          onChangeText={setFullName}
          autoCapitalize="words"
          icon="profile"
        />

        <AppInput
          testID="register-email"
          label={t('auth.register.email')}
          placeholder={t('auth.register.emailPlaceholder')}
          value={email}
          onChangeText={setEmail}
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="email-address"
          icon="mail"
        />

        <AppInput
          testID="register-password"
          label={t('auth.register.password')}
          value={password}
          onChangeText={setPassword}
          secure
          autoCapitalize="none"
          icon="lock"
          // The rule belongs under the field, not inside it: a placeholder disappears
          // the moment the user starts typing, which is exactly when they need it.
          hint={t('auth.register.passwordHint')}
          showPasswordLabel={t('a11y.showPassword')}
          hidePasswordLabel={t('a11y.hidePassword')}
          onSubmitEditing={submit}
          returnKeyType="go"
        />

        <View style={styles.consent}>
          <Pressable
            testID="register-accept-terms"
            accessibilityRole="checkbox"
            accessibilityState={{checked: accepted}}
            accessibilityLabel={t('auth.register.acceptLabel')}
            hitSlop={8}
            onPress={() => setAccepted(v => !v)}
            style={[
              styles.box,
              {
                borderColor: accepted ? p.primary : p.border,
                backgroundColor: accepted ? p.primary : 'transparent',
              },
            ]}>
            {accepted ? <Icon name="check" size={14} color={p.primaryForeground} /> : null}
          </Pressable>
          <AppText variant="body" style={styles.consentText}>
            {t('auth.register.acceptPrefix')}{' '}
            <AppText
              variant="body"
              style={link}
              accessibilityRole="link"
              onPress={() => void Linking.openURL(TERMS_URL)}>
              {t('auth.register.terms')}
            </AppText>{' '}
            {t('auth.register.acceptAnd')}{' '}
            <AppText
              variant="body"
              style={link}
              accessibilityRole="link"
              onPress={() => void Linking.openURL(PRIVACY_URL)}>
              {t('auth.register.privacy')}
            </AppText>
          </AppText>
        </View>

        {error ? (
          <Banner testID="register-error" tone="error" title={error} />
        ) : null}

        <AppButton
          testID="register-submit"
          title={t('auth.register.submit')}
          size="lg"
          onPress={submit}
          loading={busy}
        />

        <Pressable
          testID="link-login"
          style={styles.link}
          accessibilityRole="link"
          onPress={() => nav.navigate('Login')}>
          <AppText variant="label" style={link}>
            {t('auth.register.toLogin')}
          </AppText>
        </Pressable>
      </Card>
    </Screen>
  );
}
